/* eslint-disable @next/next/no-img-element */
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import Divider from '@base/Divider/Divider';
import H2 from '@base/H2/H2';
import H5 from '@base/H5/H5';
import P1 from '@base/P1/P1';
import Image from '@base/Image';
import { breakpoints, Fonts, Spacings } from '@styles/index';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import useScholarshipTestInfo from '@api/hooks/scholarship/useScholarshipTestInfo';

const Wrapper = styled.div`
  max-width: 1136px;
  margin: auto;
  margin-top: 110px;
  margin-bottom: 110px;
  display: flex;
  position: relative;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    flex-direction: column;
    padding: ${Spacings.SPACING_4B};
  }
`;

const LeftContent = styled.div`
  flex: 1;
  position: sticky;
  top: 30px;
  align-self: flex-start;
  img {
    margin-bottom: ${Spacings.SPACING_4B} !important;
  }
  @media only screen and (max-width: ${breakpoints.tablet}) {
    margin-bottom: ${Spacings.SPACING_6B};
    position: inherit;
    top: 0px;
  }
`;

const MetaDesc = styled(P1)`
  font-size: ${Fonts.LARGE};
  margin-top: ${Spacings.SPACING_4B};
  width: 90%;
`;

const RightContent = styled.div`
  flex: 2;
`;

const PrizeCard = styled.div`
  display: flex;
  box-shadow: ${boxShadowGenerator()};
  border-radius: 8px;
  padding: ${Spacings.SPACING_8B} ${Spacings.SPACING_6B};
  margin-bottom: ${Spacings.SPACING_6B};
  background: var(--color-base-1);
  overflow: hidden;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    flex-direction: column;
  }
`;

const MedalImage = styled.div`
  width: 233px;
  height: 140px;
  margin-left: ${Spacings.SPACING_2B};
  margin-right: ${Spacings.SPACING_6B};
  top: -${Spacings.SPACING_8B};
  position: relative;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    height: 80px;
    width: 133px;
    left: -10%;
  }
`;

const Title = styled(H2)`
  margin-top: ${Spacings.SPACING_2B};
`;

const StyledDivider = styled(Divider)`
  margin-top: ${Spacings.SPACING_6B};
  margin-bottom: ${Spacings.SPACING_6B};
`;

const Prize = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: ${Spacings.SPACING_3B};
`;

const PrizeLabel = styled(P1)`
  margin-left: ${Spacings.SPACING_3B};
  font-size: ${Fonts.LARGE};
  font-weight: ${({ $bold }) => ($bold ? '600' : 'normal')};
`;

const PrizesList = () => {
  const {
    query: { eventKey }
  } = useRouter();
  const { prodigyInfo } = useScholarshipTestInfo(eventKey);

  const { prizesMeta, prizes } = prodigyInfo;

  if (!prizes || !prizes.length) return null;

  return (
    <Wrapper>
      {prizesMeta && (
        <LeftContent>
          <Image src={prizesMeta.image} width={64} height={64} alt="trophy" />
          <H2>{prizesMeta.title}</H2>
          <MetaDesc>{prizesMeta.subTitle}</MetaDesc>
        </LeftContent>
      )}
      <RightContent>
        {prizes.map((prize) => {
          return (
            <PrizeCard key={`prize-${prize.id}`}>
              <MedalImage>
                <Image src={prize.rankLogo} alt="Medal" layout="fill" />
              </MedalImage>
              <div>
                <H5>{prize.rank}</H5>
                <Title>{prize.prizeTitle}</Title>
                <StyledDivider />
                {prize.prizeItems.map((item) => (
                  <Prize key={`item-${item.id}`}>
                    <Image
                      src={item.itemLogo}
                      alt="prize"
                      height={24}
                      width={24}
                    />
                    <PrizeLabel $bold={item.isBold}>{item.itemName}</PrizeLabel>
                  </Prize>
                ))}
              </div>
            </PrizeCard>
          );
        })}
      </RightContent>
    </Wrapper>
  );
};

export default PrizesList;
